import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Col,
  Container,
  Form,
  Image,
  Row,
  Table,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { adminListCategories } from "../../actions/categoryActions";
import {
  deleteJewelryHireItemAdmin,
  listJewelryHireItemsAdmin,
  resetAdminJewelryHireDelete,
} from "../../actions/jewelryHireActions";
import { adminListSubcategories } from "../../actions/subcategoryActions";
import Loader from "../../components/Loader";
import Message from "../../components/Message";
import { JEWELRY_HIRE_CATALOG_KEY } from "../../constants/shopMeta";

const getId = (value) => String(value?._id || value?.id || value || "");

const getImageUrl = (item) => {
  const image = Array.isArray(item?.images) ? item.images[0] : null;
  return typeof image === "string" ? image : image?.url || "";
};

export default function AdminJewelryHireItemsScreen() {
  const dispatch = useDispatch();
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [keyword, setKeyword] = useState("");
  const [search, setSearch] = useState("");

  const itemState = useSelector((state) => state.adminJewelryHireList || {});
  const deleteState = useSelector(
    (state) => state.adminJewelryHireDelete || {},
  );
  const categoryState = useSelector(
    (state) => state.adminCategoryList || {},
  );
  const subcategoryState = useSelector(
    (state) => state.adminSubcategoryList || {},
  );

  const items = Array.isArray(itemState.items) ? itemState.items : [];
  const categories = Array.isArray(categoryState.categories)
    ? categoryState.categories
    : [];
  const subcategories = useMemo(
    () =>
      Array.isArray(subcategoryState.subcategories)
        ? subcategoryState.subcategories
        : [],
    [subcategoryState.subcategories],
  );
  const visibleSubcategories = useMemo(
    () =>
      subcategories.filter(
        (candidate) => !category || getId(candidate.parentId) === category,
      ),
    [category, subcategories],
  );

  const loadItems = () =>
    dispatch(
      listJewelryHireItemsAdmin({
        category,
        subcategory,
        keyword: search,
        pageSize: 100,
      }),
    ).catch(() => undefined);

  useEffect(() => {
    dispatch(
      adminListCategories({
        shopKey: JEWELRY_HIRE_CATALOG_KEY,
        pageSize: 200,
      }),
    ).catch(() => undefined);
    dispatch(
      adminListSubcategories({
        shopKey: JEWELRY_HIRE_CATALOG_KEY,
        pageSize: 200,
      }),
    ).catch(() => undefined);
  }, [dispatch]);

  useEffect(() => {
    loadItems();
    // loadItems is intentionally derived from the active filters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, dispatch, search, subcategory]);

  useEffect(() => {
    if (deleteState.success) {
      dispatch(resetAdminJewelryHireDelete());
      loadItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteState.success, dispatch]);

  const submitSearch = (event) => {
    event.preventDefault();
    setSearch(keyword.trim());
  };

  const deleteHandler = async (item) => {
    const id = getId(item);
    if (!id) return;

    const confirmed = window.confirm(
      `Delete jewellery hire item "${item.name || "Untitled"}"?`,
    );
    if (!confirmed) return;

    try {
      await dispatch(deleteJewelryHireItemAdmin(id));
      toast.success("Jewellery hire item deleted.");
    } catch {
      // The reducer displays the API error below.
    }
  };

  const taxonomyError = categoryState.error || subcategoryState.error;

  return (
    <Container className="py-4">
      <Row className="align-items-center g-3 mb-3">
        <Col>
          <h1>Admin • Jewellery Hire</h1>
          <div className="text-muted">
            Manage catalogue details, imagery and display order.
          </div>
        </Col>
        <Col md="auto" className="d-flex gap-2 flex-wrap">
          <Button
            as={Link}
            to={`/admin/shop/${JEWELRY_HIRE_CATALOG_KEY}/categories`}
            variant="outline-dark"
          >
            Categories
          </Button>
          <Button
            as={Link}
            to={`/admin/shop/${JEWELRY_HIRE_CATALOG_KEY}/subcategories`}
            variant="outline-dark"
          >
            Subcategories
          </Button>
          <Button as={Link} to="/admin/jewelry-hire/new" variant="dark">
            Create item
          </Button>
        </Col>
      </Row>

      {taxonomyError ? <Message variant="danger">{taxonomyError}</Message> : null}
      {deleteState.loading ? <Message>Deleting item...</Message> : null}
      {deleteState.error ? (
        <Message variant="danger">{deleteState.error}</Message>
      ) : null}

      <Form onSubmit={submitSearch} className="mb-4">
        <Row className="g-3 align-items-end">
          <Col md={4}>
            <Form.Group controlId="jewelryAdminCategory">
              <Form.Label>Category</Form.Label>
              <Form.Select
                value={category}
                onChange={(event) => {
                  setCategory(event.target.value);
                  setSubcategory("");
                }}
                disabled={categoryState.loading}
              >
                <option value="">All categories</option>
                {categories.map((candidate) => (
                  <option key={getId(candidate)} value={getId(candidate)}>
                    {candidate.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="jewelryAdminSubcategory">
              <Form.Label>Subcategory</Form.Label>
              <Form.Select
                value={subcategory}
                onChange={(event) => setSubcategory(event.target.value)}
                disabled={subcategoryState.loading}
              >
                <option value="">All subcategories</option>
                {visibleSubcategories.map((candidate) => (
                  <option key={getId(candidate)} value={getId(candidate)}>
                    {candidate.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group controlId="jewelryAdminSearch">
              <Form.Label>Search</Form.Label>
              <Form.Control
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="Name or description"
              />
            </Form.Group>
          </Col>
          <Col md={1}>
            <Button type="submit" variant="outline-dark" className="w-100">
              Go
            </Button>
          </Col>
        </Row>
      </Form>

      {itemState.loading ? <Loader /> : null}
      {itemState.error ? (
        <Message variant="danger">{itemState.error}</Message>
      ) : null}

      {!itemState.loading && !itemState.error && items.length === 0 ? (
        <Message>No jewellery hire items found.</Message>
      ) : null}

      {items.length > 0 ? (
        <Table striped bordered hover responsive className="align-middle">
          <thead>
            <tr>
              <th style={{ width: 72 }}>Image</th>
              <th>Item</th>
              <th>Category</th>
              <th>Display order</th>
              <th>Published</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const id = getId(item);
              const imageUrl = getImageUrl(item);

              return (
                <tr key={id}>
                  <td>
                    {imageUrl ? (
                      <Image
                        src={imageUrl}
                        alt={item.name || "Jewellery item"}
                        rounded
                        style={{ width: 48, height: 48, objectFit: "cover" }}
                      />
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                  <td>
                    <div className="fw-semibold">{item.name}</div>
                    <div className="small text-muted">/{item.slug}</div>
                  </td>
                  <td>
                    <div>{item.category?.name || "—"}</div>
                    <div className="small text-muted">
                      {item.subcategory?.name || "—"}
                    </div>
                  </td>
                  <td>{item.displayOrder ?? 0}</td>
                  <td>
                    <Badge bg={item.isPublished ? "success" : "secondary"}>
                      {item.isPublished ? "Published" : "Draft"}
                    </Badge>
                  </td>
                  <td className="text-end text-nowrap">
                    <Button
                      as={Link}
                      to={`/admin/jewelry-hire/${id}/edit`}
                      variant="light"
                      size="sm"
                      className="me-2"
                    >
                      Edit
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => deleteHandler(item)}
                      disabled={deleteState.loading}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      ) : null}
    </Container>
  );
}
